import React, { ComponentType } from 'react';
import { Container, Row, Column, Direction } from '@uds/grid';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import Ribbon from 'components/ribbons/Ribbon';
import CallToAction from 'components/common/CallToAction';
import PaddingBox from '@uds/padding-box';
import { useBreakpoints } from '@ux/responsive';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import visibilitySet from 'presentation/hocs/visibilitySet';
import errorBoundary from 'presentation/hocs/errorBoundary';
import flexAnchors from 'presentation/utils/flexAnchors';
import { Anchor } from 'domain/constants/anchor';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { Dimension } from 'domain/constants/dimension';
import { CTA } from 'core/ribbons/cta';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import { Layout } from 'domain/constants/layout';
import ribbonPadding from 'presentation/utils/ribbonPadding';
import { Heading02, BodyCopy, Heading04, Heading05, Typelockup } from '@uds/typography';

import { getClassNames, StyledRoot } from './Centerfold.stylesheet';
import CenterfoldImage from './Image';

const setDirection = (layout: Layout) => {
  if (layout.constrainImage) {
    if (layout.flipped) {
      return Direction.ROW;
    }
    return Direction.ROW_REVERSE;
  }
  return Direction.ROW;
};

const needsOffset = (layout: Layout, isMinSm: boolean) => layout.flipped && isMinSm;

const setOffset = (layout: Layout) => (layout.constrainImage ? 0 : 6);

type Text = {
  title: ShortcodeProps;
  body: ShortcodeProps;
  ctas: Array<CTA>;
  subtitle?: ShortcodeProps;
  precursor?: ShortcodeProps;
};

export type Image = {
  url: string;
  focus: string;
  preserveDimension?: Dimension;
};

export interface OuterProps extends RibbonProps {
  text: Text;
  image: Array<Image>;
}

export const PureCenterfold = ({ id, loading, name, text, layout, colorway, hBaseline = 1, image }: OuterProps) => {
  const { title, body, ctas, subtitle, precursor } = text || {};

  const { constrainImage } = layout;

  const breakpoints = useBreakpoints();
  const { isMinSm, isMinLg } = breakpoints;

  const activeImage = findResponsiveItem(breakpoints, image) as Image;

  const activeAnchor = findResponsiveItem(breakpoints, flexAnchors([Anchor.CENTER, null, null], layout.anchor), {
    supportNull: true,
  });

  const offset = needsOffset(layout, isMinSm) ? setOffset(layout) : null;

  const hasTitle = !isNilOrEmpty(title);
  const hasBody = !isNilOrEmpty(body);
  const hasCta = !isNilOrEmpty(ctas);
  const hasSubtitle = !isNilOrEmpty(subtitle);
  const hasPrecursor = !isNilOrEmpty(precursor);

  const classNames = getClassNames({ text, layout });

  return (
    <Ribbon
      id={id}
      colorway={colorway}
      layout={layout}
      loading={loading}
      flush={true}
      name={name}
      type={Type.CENTERFOLD}
    >
      <StyledRoot
        className={classNames.root}
        layout={layout}
        // @ts-ignore style doesn't understand this, but it's valid
        style={ribbonPadding({ layout, isMinLg, isMinSm, linariaPadding: true })}
      >
        {!constrainImage && <CenterfoldImage image={activeImage} layout={layout} />}
        <div className={classNames.heightAnchor}>
          <Container vblock={true}>
            {/* @ts-ignore: error after upgrading to ux-configs */}
            <Row vblock={true} direction={setDirection(layout)}>
              {constrainImage && (
                <Column xs={12} sm={6}>
                  <CenterfoldImage image={activeImage} layout={layout} />
                </Column>
              )}
              <Column
                xs={12}
                sm={6}
                // @ts-ignore: error after upgrading to ux-configs
                offsetXs={offset}
                align={activeAnchor}
              >
                <PaddingBox className={classNames.content}>
                  <Typelockup
                    eyebrow={
                      hasPrecursor ? (
                        <Heading05 component="p">
                          <Shortcode code={precursor} against={colorway} />
                        </Heading05>
                      ) : undefined
                    }
                    // @ts-ignore heading can apparently be undefined. LOL.
                    heading={
                      hasTitle ? (
                        <Heading02 depth={hBaseline}>
                          <Shortcode code={title} against={colorway} />
                        </Heading02>
                      ) : undefined
                    }
                    subheading={
                      hasSubtitle ? (
                        <Heading04 component="p">
                          <Shortcode code={subtitle} against={colorway} />
                        </Heading04>
                      ) : undefined
                    }
                  />

                  {hasBody && (
                    <BodyCopy className={classNames.text}>
                      <Shortcode code={body} against={colorway} />
                    </BodyCopy>
                  )}
                  {hasCta && (
                    <div className={classNames.ctas}>
                      {ctas.map((cta, i) => (
                        <div
                          className={classNames.action}
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                        >
                          <CallToAction ribbonId={id} index={i} {...cta} />
                        </div>
                      ))}
                    </div>
                  )}
                </PaddingBox>
              </Column>
            </Row>
          </Container>
        </div>
      </StyledRoot>
    </Ribbon>
  );
};

export default visibilitySet(errorBoundary(PureCenterfold)) as ComponentType<OuterProps>;
